import React, { useState } from 'react';
import { Link, useNavigate, Navigate } from 'react-router-dom';
import { useAuth, ROLE_LABEL } from '@/lib/auth';
import { PemkotLogo } from '@/components/PemkotLogo';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { Eye, EyeOff, ShieldCheck, Loader2 } from 'lucide-react';

export default function LoginPage() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const u = await login(email, password);
      toast.success(`Selamat datang, ${u.full_name.split(' ')[0]}!`);
      if (u.role === 'pimpinan') navigate('/command-center');
      else navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Login gagal');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden bg-slate-50">
      {/* Decorative background */}
      <div className="absolute inset-0 hero-warm" />
      <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-red-600/10 blur-3xl" />
      <div className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-amber-400/10 blur-3xl" />

      <div className="relative z-10 min-h-screen grid lg:grid-cols-5">
        {/* Left brand panel */}
        <div className="hidden lg:flex lg:col-span-2 flex-col justify-between p-12 text-slate-900">
          <div className="flex items-center gap-3">
            <PemkotLogo size={56} />
            <div className="flex flex-col leading-tight">
              <span className="text-[11px] uppercase tracking-[0.22em] text-slate-500 font-semibold">Pemerintah Kota</span>
              <span className="text-xl font-extrabold">Makassar</span>
            </div>
          </div>

          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/70 backdrop-blur border border-slate-200 text-xs font-semibold text-slate-600 mb-6">
              <ShieldCheck className="w-3.5 h-3.5 text-[#DC2626]" />
              Command Center Prokopim - SPBE Pemkot Makassar
            </div>
            <h1 className="text-5xl xl:text-6xl font-extrabold leading-tight" style={{ fontFamily: 'Plus Jakarta Sans' }}>
              PRO<span className="text-[#DC2626]">AKTIF</span>
            </h1>
            <p className="mt-3 text-base text-slate-600 max-w-md">
              Protokol Andal, Koordinatif, Terencana, Inovatif, dan Fleksibel — sistem terpusat agenda pimpinan & monitoring perjalanan dinas Pemerintah Kota Makassar.
            </p>

            <div className="mt-8 grid grid-cols-3 gap-4 max-w-md">
              <div>
                <div className="text-2xl font-extrabold tabular text-slate-900">20+</div>
                <div className="text-[11px] text-slate-500 uppercase tracking-wider">OPD Terintegrasi</div>
              </div>
              <div>
                <div className="text-2xl font-extrabold tabular text-slate-900">5</div>
                <div className="text-[11px] text-slate-500 uppercase tracking-wider">Role Pengguna</div>
              </div>
              <div>
                <div className="text-2xl font-extrabold tabular text-slate-900">24/7</div>
                <div className="text-[11px] text-slate-500 uppercase tracking-wider">Real-time Monitor</div>
              </div>
            </div>
          </div>

          <div className="text-xs text-slate-500">
            &copy; {new Date().getFullYear()} Pemerintah Kota Makassar · Bagian Protokol dan Komunikasi Pimpinan
          </div>
        </div>

        {/* Right login form */}
        <div className="col-span-1 lg:col-span-3 flex items-center justify-center p-6 md:p-12">
          <div className="w-full max-w-md fade-up">
            <div className="lg:hidden flex items-center gap-3 mb-6">
              <PemkotLogo size={44} />
              <div className="flex flex-col leading-tight">
                <span className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">Pemkot Makassar</span>
                <span className="text-lg font-extrabold">PRO<span className="text-[#DC2626]">AKTIF</span></span>
              </div>
            </div>

            <Card className="border-slate-200 shadow-elevated">
              <CardContent className="p-6 md:p-8">
                <h2 className="text-2xl font-extrabold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>Masuk ke PROAKTIF</h2>
                <p className="text-sm text-slate-500 mt-1">Silakan masuk dengan akun resmi Pemkot Makassar Anda.</p>

                <form onSubmit={submit} className="mt-6 space-y-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="email">Email / NIP</Label>
                    <Input
                      id="email"
                      data-testid="login-email-input"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="nama@makassarkota.go.id"
                      required
                      autoComplete="email"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="password">Password</Label>
                    <div className="relative">
                      <Input
                        id="password"
                        data-testid="login-password-input"
                        type={showPwd ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        autoComplete="current-password"
                        className="pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPwd((v) => !v)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600"
                        aria-label="Toggle password"
                      >
                        {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={submitting}
                    data-testid="login-submit-button"
                    className="w-full bg-[#DC2626] hover:bg-[#B91C1C] text-white font-semibold h-11 text-base shadow-premium"
                  >
                    {submitting ? (
                      <span className="inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Memproses…</span>
                    ) : 'Masuk'}
                  </Button>
                </form>
              </CardContent>
            </Card>

            <p className="text-[11px] text-center text-slate-500 mt-6">
              Sistem ini dilindungi. Semua aktivitas dicatat dalam audit trail SPBE.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
